'use client';

import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, ExternalLink } from 'lucide-react';
import { getCaseDocuments, getCaseDocumentUrl, type CaseDocument } from '@/lib/supabaseData';

interface CaseDocumentsPublicProps {
  caseId: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CaseDocumentsPublic({ caseId }: CaseDocumentsPublicProps) {
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    getCaseDocuments(caseId).then((docs) => {
      setDocuments(docs);
      setIsLoading(false);
    });
  }, [caseId]);

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-center gap-3">
        <Loader2 size={16} className="animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Cargando documentos...</p>
      </div>
    );
  }

  if (documents.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
          <FileText size={16} className="text-accent" />
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Documentos del expediente
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {documents.length} documento{documents.length !== 1 ? 's' : ''} disponible{documents.length !== 1 ? 's' : ''} para descarga
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {documents.map((doc) => (
          <a
            key={doc.id}
            href={getCaseDocumentUrl(doc.filePath)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-accent/5 border border-border hover:border-accent/30 rounded-lg transition-all duration-150 group"
          >
            <div className="w-9 h-9 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              <FileText size={16} className="text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors truncate">
                {doc.fileName}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatFileSize(doc.fileSize)} ·{' '}
                {new Date(doc.uploadedAt).toLocaleDateString('es-CO', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-accent opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <Download size={14} />
              Descargar
            </div>
            <ExternalLink size={14} className="text-muted-foreground/40 group-hover:text-accent/60 transition-colors flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}
